import { Search } from '@mui/icons-material';
import { Button, TextField } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import dayjs from 'dayjs';
import { useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useUserNodes } from '/@/hooks/nodes';
import { useHasWriteAccess } from '/@/hooks/session';
import { _ } from '/@/locale';
import MuiDatePicker from '/@/mui/datepicker';
import { MuiSelect } from '/@/mui/select';
import NewFieldGrowersModal from '/@/views/dashboard/growers/components/new-field-modal';
import { getCrops } from '/@/views/dashboard/hooks/crops';
import type { FieldListV2FormType } from '../..';

const FieldListV2FormFilters = ({
    form,
    userNodeId,
    includeChildrenNodes,
}: {
    form: UseFormReturn<FieldListV2FormType>;
    userNodeId: number;
    includeChildrenNodes?: boolean;
}) => {
    const growersQuery = useUserNodes({
        node: userNodeId,
        page: 1,
        page_size: 10000,
        type: 'grower',
    });
    const [newFieldModalOpen, setNewFieldModalOpen] = useState(false);
    const hasWriteAccess = useHasWriteAccess();
    const crops = getCrops();
    return (
        <Grid2 container spacing={2} mx={'-8px !important'} alignItems='center'>
            <Grid2 xs>
                <TextField
                    size='small'
                    InputProps={{
                        startAdornment: <Search />,
                    }}
                    label={_('Buscar lote por nombre')}
                    {...form.register('name')}
                    fullWidth
                    data-testid='field-list-v2-form-filters-input-name'
                />
            </Grid2>
            {includeChildrenNodes && (
                <Grid2 xs={6} sm={2} className='mp-mask'>
                    <MuiSelect
                        size='small'
                        {...form.register('producerId')}
                        type='number'
                        value={form.watch('producerId')}
                        inputLabel={_('Productor')}
                        options={(growersQuery.data?.results || []).map((grower) => ({
                            label: grower.isSelf ? _('Mi cuenta') : grower.name,
                            value: grower.id.toString(),
                        }))}
                        formControlProps={{
                            fullWidth: true,
                        }}
                        clearable
                    />
                </Grid2>
            )}
            <Grid2 xs={6} sm={2}>
                <MuiSelect
                    size='small'
                    {...form.register('crop')}
                    value={form.watch('crop')}
                    inputLabel={_('Cultivos')}
                    options={Object.values(crops).map((crop) => ({ label: crop.name, value: crop.value }))}
                    formControlProps={{
                        fullWidth: true,
                    }}
                    clearable
                />
            </Grid2>
            <Grid2 xs={6} sm={2}>
                <MuiDatePicker
                    size='small'
                    label={_('Mes de siembra')}
                    {...form.register('plantingDateTo')}
                    slotProps={{ field: { clearable: true } }}
                    onChange={(value) => {
                        const month = value?.month();
                        const year = value?.year();
                        if (month && year) {
                            form.setValue(
                                'plantingDateFrom',
                                dayjs().set('month', month).set('year', year).startOf('month')
                            );
                            form.setValue(
                                'plantingDateTo',
                                dayjs().set('month', month).set('year', year).endOf('month')
                            );
                        } else {
                            form.setValue('plantingDateFrom', null);
                            form.setValue('plantingDateTo', null);
                        }
                    }}
                    views={['month', 'year']}
                    maxDate={dayjs().add(1, 'year')}
                    minDate={dayjs().subtract(10, 'year')}
                    fullWidth
                />
            </Grid2>
            {hasWriteAccess && (
                <Grid2 xs={6} sm={'auto'}>
                    <Button
                        variant='contained'
                        color='secondary'
                        onClick={() => {
                            setNewFieldModalOpen(true);
                        }}
                        fullWidth
                        data-testid='add-field-button-rv-home'
                        disabled={growersQuery.data?.count === 0}
                    >
                        {_('Agregar lote')}
                    </Button>
                </Grid2>
            )}

            <NewFieldGrowersModal
                open={newFieldModalOpen}
                setOpen={setNewFieldModalOpen}
                rvUserNodeId={userNodeId}
                growerUserNodeId={includeChildrenNodes ? undefined : userNodeId}
                trackEntrySource='field-list'
            />
        </Grid2>
    );
};

export default FieldListV2FormFilters;
